import React from "react";
import { Card, CardHeader, FormField, ToggleSwitch } from "../../UI/DesignStandards";
import { sanitizeUsbcId } from "../../../utils/sanitization";

const emptySlot = () => ({
  name: "",
  email: "",
  phone: "",
  usbcId: "",
  average: "",
  alsoBowlSingles: false,
});

export const bowlerSlotsForEvent = (event, existingSlots = []) => {
  const count = Math.max(1, Number(event?.bowlerQuantity) || 1);
  const next = [];
  for (let i = 0; i < count; i += 1) {
    next.push({ ...emptySlot(), ...(existingSlots[i] || {}) });
  }
  return next;
};

export const formatPhoneNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  const cleaned =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

const EntryBowlersCard = ({
  event,
  slots,
  errors = [],
  signupSettings,
  showSinglesToggle = false,
  everyoneBowlsSingles = false,
  signedIn = false,
  primaryAccountEmail = "",
  primaryAccountName = "",
  onSlotChange,
}) => {
  if (!event) return null;

  return (
    <Card className="mb-8">
      <CardHeader
        title="Bowlers"
        subtitle={
          Number(event.bowlerQuantity) > 1
            ? `Enter all ${event.bowlerQuantity} bowlers for ${event.eventName}.`
            : `Enter the bowler for ${event.eventName}.`
        }
      />
      <div className="p-6 space-y-8">
        {everyoneBowlsSingles ? (
          <p className="text-sm text-brand-muted">
            Add to cart also places Singles for each of these bowlers on this
            squad.
          </p>
        ) : null}
        {slots.map((slot, index) => {
          const fieldErrors = errors[index] || {};
          const label = index === 0 ? "Primary bowler" : `Teammate ${index}`;
          return (
            <div key={`bowler-${index}`} className="space-y-4">
              <h3 className="text-sm font-semibold text-brand-charcoal">
                {label}
              </h3>
              {index === 0 && signedIn && primaryAccountName ? (
                <p className="text-sm text-brand-muted">
                  {primaryAccountName}
                  {primaryAccountEmail
                    ? ` · ${primaryAccountEmail}`
                    : ""}
                </p>
              ) : (
                <div {...(fieldErrors.name ? { "data-signup-error": "" } : {})}>
                  <FormField
                    label="Full name"
                    type="text"
                    value={slot.name}
                    onChange={(e) => onSlotChange(index, "name", e.target.value)}
                    required
                    error={fieldErrors.name}
                  />
                </div>
              )}
              {index === 0 && signedIn ? (
                primaryAccountName ? null : primaryAccountEmail ? (
                  <p className="text-sm text-brand-muted">
                    Uses your account email, {primaryAccountEmail}.
                  </p>
                ) : null
              ) : signupSettings?.requireEmail || slot.email ? (
                <div {...(fieldErrors.email ? { "data-signup-error": "" } : {})}>
                  <FormField
                    label="Email"
                    type="email"
                    value={slot.email}
                    onChange={(e) => onSlotChange(index, "email", e.target.value)}
                    required={Boolean(signupSettings?.requireEmail)}
                    error={fieldErrors.email}
                  />
                </div>
              ) : null}
              {signupSettings?.requirePhone || slot.phone ? (
                <div {...(fieldErrors.phone ? { "data-signup-error": "" } : {})}>
                  <FormField
                    label="Phone"
                    type="tel"
                    value={slot.phone}
                    onChange={(e) =>
                      onSlotChange(
                        index,
                        "phone",
                        formatPhoneNumber(e.target.value)
                      )
                    }
                    required={Boolean(signupSettings?.requirePhone)}
                    error={fieldErrors.phone}
                  />
                </div>
              ) : null}
              {signupSettings?.requireUSBCID || slot.usbcId ? (
                <div {...(fieldErrors.usbcId ? { "data-signup-error": "" } : {})}>
                  <FormField
                    label="USBC ID"
                    type="text"
                    value={slot.usbcId}
                    onChange={(e) =>
                      onSlotChange(index, "usbcId", sanitizeUsbcId(e.target.value))
                    }
                    required={Boolean(signupSettings?.requireUSBCID)}
                    error={fieldErrors.usbcId}
                  />
                </div>
              ) : null}
              {signupSettings?.requireAverage || slot.average ? (
                <div {...(fieldErrors.average ? { "data-signup-error": "" } : {})}>
                  <FormField
                    label="Average"
                    type="number"
                    value={slot.average}
                    onChange={(e) =>
                      onSlotChange(index, "average", e.target.value)
                    }
                    min="0"
                    max="300"
                    required={Boolean(signupSettings?.requireAverage)}
                    error={fieldErrors.average}
                  />
                </div>
              ) : null}
              {showSinglesToggle ? (
                <label className="flex items-center gap-3 text-sm text-brand-charcoal">
                  <ToggleSwitch
                    checked={Boolean(slot.alsoBowlSingles)}
                    onChange={(checked) =>
                      onSlotChange(index, "alsoBowlSingles", checked)
                    }
                  />
                  Also bowl Singles
                </label>
              ) : null}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default EntryBowlersCard;
